"use client";

import { useContext, useState, useEffect } from "react";
import { useRouter } from "next/navigation";

import { QRSelectorGrid } from "@/components/QRSelectorGrid";
import { Typography } from "@/components/Typography";
import { Accordion } from "@/components/Accordion";
import { Chip } from "@/components/Chip";

import { getSiteInfo } from "@/sites";
import { useQR } from "@/hooks/qr/useQR";
import { useToast } from "@/hooks/ui/useToast";

import { handleNextButton } from "@/utils/qrEditorNavigatorHelper";
import { setNewQRData, generateQrDataClone } from "@/utils/qrEditorHelper";
import { getDynamicQr, getStaticQr } from "@/utils/qrContentType";

import { amplitudeTrackEvent } from "@/services/AmplitudeService/AmplitudeService";
import { isUserFreeTrial } from "@/services/UserService/UserService";

import { UserContext } from "@/contexts/UserContext";
import { GlobalContext } from "@/contexts/GlobalContext";
import { QREditorContext } from "@/contexts/QREditorContext";
import { QREditorContextProps } from "@/contexts/QREditorContext/types";
import { AmplitudeContext } from "@/contexts/AmplitudeContext/AmplitudeContext";

import { QRType, QRTypeName } from "@/types/qr";
import { QREditorSelectorProps } from "./types";
import { useTranslation } from "react-i18next";
import { PAGES } from "@/internals/constants/pages";
import "./styles.scss";

export const QREditorSelectorTemplate = (props: QREditorSelectorProps) => {
  const { t } = useTranslation(PAGES.QR_EDITOR_LAYOUT);
  const { data } = props;
  const { toast } = useToast();
  const router = useRouter();

  const userContext = useContext(UserContext);
  const { source, setSource } = useContext(AmplitudeContext);
  const qrEditorContext = useContext<QREditorContextProps<any>>(QREditorContext);
  const globalContext = useContext(GlobalContext);
  const [qrTypeSelected, setQrTypeSelected] = useState<QRType | string>("");
  const [isQRSelected, setIsQRSelected] = useState(false);

  const { requestQR } = useQR();

  const hoverChangeQRType = (type: QRType, name: QRTypeName) => {
    setQrTypeSelected(type);
    if (!isQRSelected) setNewQRData(qrEditorContext, type, name);
  };

  const onClickQRselect = (type: QRType, name: QRTypeName) => {
    hoverChangeQRType(type, name);
    setIsQRSelected(true);
    setSource("cta_buttons");
    qrEditorContext?.setEventSource("qr-editor");
  };

  const getTitle = () => {
    const { isFreeTrial } = getSiteInfo(process.env.NEXT_PUBLIC_DOMAIN);
    const isAnonUser = !userContext?.user?.userId;

    const showTitleFree = isFreeTrial && (isUserFreeTrial(userContext) || isAnonUser);
    return showTitleFree ? data?.titleFree : data?.title;
  };

  useEffect(() => {
    setIsQRSelected(false);
    qrEditorContext?.setValidationError(false);
    qrEditorContext?.setCreateNewTemplate(false);

    const newQRData = generateQrDataClone(qrEditorContext, "");
    if (source) {
      amplitudeTrackEvent("view_editor_select_qr ", {
        source,
      });
    } else {
      amplitudeTrackEvent("view_editor_select_qr", {
        source: "browser_url",
      });
    }
    if (newQRData) newQRData.currentPageIndex = 0;
    setTimeout(() => {
      qrEditorContext?.setQrData(newQRData);
    }, 0);
  }, []);

  useEffect(() => {
    if (isQRSelected) {
      setTimeout(() => {
        handleNextButton(
          globalContext,
          qrEditorContext,
          userContext,
          requestQR,
          toast,
          true,
          router,
          setSource,
          t
        );
      }, 0);
    }
  }, [isQRSelected]);

  useEffect(() => {
    if (source) setNewQRData(qrEditorContext, qrTypeSelected);
  }, [source]);

  return (
    <div
      className="qr-selector"
      data-testid="QRHOme"
    >
      <div className="qr-selector__layout">
        <Typography
          Tag="p"
          size="xl"
          classname="u-bold u-spacing-5"
        >
          {getTitle()}
        </Typography>

        <div className="qr-type__header">
          <div className="qr-type__header__title">
            <Typography
              Tag="p"
              size="m"
              classname="u-bold"
            >
              {data.dynamicQrTitle}
            </Typography>
            <Chip
              label={data.dynamicQrChipLabel}
              type="filled"
              className="chip--success"
            />
          </div>

          <Typography
            Tag="p"
            size="l"
            classname="body-xs u-grey-700"
          >
            {data.dynamicQrSubtitle}
          </Typography>
        </div>
        <QRSelectorGrid
          data={getDynamicQr(data)}
          onHoverChangeQRType={(id, title) => hoverChangeQRType(id, title)}
          onClickQRselect={(id, title) => onClickQRselect(id, title)}
        />

        <Accordion
          title={
            <div className="qr-type__header">
              <div className="qr-type__header__title">
                <Typography
                  Tag="p"
                  size="m"
                  classname="u-bold"
                >
                  {data.staticQrTitle}
                </Typography>
                <Chip
                  label={data.staticQrChipLabel}
                  type="filled"
                  className="chip--warning"
                />
              </div>
            </div>
          }
        >
          <Typography
            Tag="p"
            size="l"
            classname="body-xs u-grey-700"
          >
            {data.staticQrSubtitle}
          </Typography>
          <div className="qr-type__container">
            <QRSelectorGrid
              data={getStaticQr(data)}
              onHoverChangeQRType={(id, title) => hoverChangeQRType(id, title)}
              onClickQRselect={(id, title) => onClickQRselect(id, title)}
            />
          </div>
        </Accordion>
      </div>
    </div>
  );
};
